import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCD } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { capitalizeWords, formatContent, formatDate } from '../utils/cdUtils';

const ViewCDDetailPage = () => {
  const { id } = useParams();
  const [cd, setCD] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  // Company header wrapper with white background
  const companyHeaderWrapperStyle = {
    background: 'rgb(20, 32, 57)',  // Navy blue background
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
  };

  // Result container wrapper and inner styles
  const resultContainerWrapperStyle = {
    background: 'white',
    borderRadius: '0.75rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden',
    margin: '0 auto',
    padding: '0'
  };

  const resultContainerStyle = {
    background: 'linear-gradient(to right, rgba(20, 32, 57, 0.9), rgba(30, 41, 59, 0.8))',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    height: '100%',
    width: '100%'
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchCD = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getCD(id);
        setCD(result);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          logout();
          navigate('/login');
        } else {
          setError('Failed to fetch research details. Please try again.');
          console.error('Error fetching CD details:', error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchCD();
  }, [id, isAuthenticated, logout, navigate]);

  const handleBack = () => {
    navigate('/view-cds');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-royal-900 to-royal-800 py-12 px-4 sm:px-6 lg:px-8 font-poppins text-white">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-center min-h-[60vh]">
          <div className="relative w-24 h-24">
            <div className="absolute top-0 left-0 w-full h-full border-8 border-white/20 rounded-full"></div>
            <div className="absolute top-0 left-0 w-full h-full border-8 border-transparent border-t-orange-500 rounded-full animate-spin"></div>
          </div>
          <p className="mt-6 text-xl font-medium text-white">Loading research details...</p>
          <p className="mt-2 text-white/70">Fetching comprehensive company insights</p>
        </div>
      </div>
    );
  }

  if (error || !cd) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-royal-900 to-royal-800 py-12 px-4 sm:px-6 lg:px-8 font-poppins text-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-500/20 border-l-4 border-red-500 p-6 rounded-lg mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-300">Error</h3>
                <p className="mt-1 text-white">{error || 'Research report not found'}</p>
              </div>
            </div>
          </div>
          <button
            onClick={handleBack}
            className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-opacity-50 transition-all duration-200"
          >
            ← Back to Research List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-royal-900 to-royal-800 py-12 px-4 sm:px-6 lg:px-8 font-poppins text-white">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-opacity-50 transition-all duration-200 flex items-center"
          >
            <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Research List
          </button>
        </div>
        
        {/* Research Content - With white backing and gradient */}
        <div style={resultContainerWrapperStyle}>
          <div style={resultContainerStyle}>
            {/* Company Header - Navy blue background */}
            <div style={companyHeaderWrapperStyle} className="py-6 px-8">
              <h2 className="text-3xl font-bold mb-2 font-playfair text-orange-500">
                {capitalizeWords(cd.company_name)}
              </h2>
              <div className="flex flex-wrap items-center gap-4 text-gray-300 text-sm">
                <span>📅 {formatDate(cd.timestamp)}</span>
                <span>🌐 {cd.company_website}</span>
                {cd.stakeholders && (
                  <span>👥 {cd.stakeholders}</span>
                )}
              </div>
            </div>
            
            <div className="p-8">
              <table className="w-full border-collapse" style={{tableLayout: 'fixed', wordWrap: 'break-word'}}>
                <tbody>
                  {formatContent(cd.response)}
                </tbody>
              </table>
            </div>
            
            {cd.additional_resources && (
              <div className="px-8 pb-8">
                {/* Additional Resources header - With white background and orange gradient text */}
                <div className="relative overflow-hidden rounded-md shadow-md my-5 bg-white">
                  <div className="absolute inset-0 bg-gradient-to-r from-rgba(20, 32, 57, 0.1) to-rgba(30, 41, 59, 0.05)"></div>
                  <div className="relative px-5 py-4 z-10">
                    <span className="bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent font-bold text-xl font-playfair">
                      Additional Resources
                    </span>
                  </div>
                </div>
                <ol className="list-decimal pl-8 mt-2 space-y-3 text-white">
                  {JSON.parse(cd.additional_resources).slice(0, 10).map((resource, index) => (
                    <li key={index} className="mb-2 hover:text-orange-300 transition-colors duration-200">
                      <a 
                        href={resource.link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-orange-400 hover:text-orange-300 break-words underline underline-offset-2 decoration-1 hover:decoration-2 transition-all duration-200"
                      >
                        {resource.title}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewCDDetailPage;